import { SCORING } from '@/mocks/challenges';
import { LEADERBOARD_DATA, AVATAR_GRADIENTS } from '@/mocks/leaderboard';
import { Link } from 'react-router-dom';

const SCORE_ITEMS = [
  { label: 'Correct classification', points: SCORING.classification },
  { label: 'Correct severity', points: SCORING.severity },
  { label: 'Correct next action', points: SCORING.nextAction },
];

export default function ScoringSection() {
  const topThree = LEADERBOARD_DATA.slice(0, 4);

  return (
    <section className="py-24 bg-bg-surface border-t border-border-subtle">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="text-center mb-14">
          <div className="inline-block text-xs font-mono uppercase tracking-widest text-accent mb-3">
            Scoring &amp; rankings
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-text-primary">
            Earn points. Build your instinct.
          </h2>
          <p className="text-text-secondary mt-3 max-w-2xl mx-auto text-sm leading-relaxed">
            The scoring system is simple, but the value comes from repetition. Small daily reps
            build stronger production judgement over time.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-bg-panel border border-border-subtle rounded-xl p-6">
            <h3 className="font-semibold text-text-primary text-base mb-5 flex items-center gap-2">
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-trophy-line text-accent" />
              </span>
              Scoring System
            </h3>
            <div className="space-y-3">
              {SCORE_ITEMS.map((item) => (
                <div
                  key={item.label}
                  className="flex items-center justify-between py-3 border-b border-border-subtle"
                >
                  <span className="text-sm text-text-secondary font-mono">{item.label}</span>
                  <span className="font-mono font-semibold text-accent text-sm">
                    +{item.points} pts
                  </span>
                </div>
              ))}
              <div className="flex items-center justify-between py-3 bg-accent/10 rounded-lg px-3 mt-2">
                <span className="text-sm font-semibold text-text-primary font-mono">
                  Perfect score
                </span>
                <span className="font-mono font-bold text-accent text-base">
                  {SCORING.perfect} pts
                </span>
              </div>
            </div>
            <div className="mt-5 pt-5 border-t border-border-subtle">
              <div className="flex items-start gap-3">
                <span className="w-8 h-8 flex items-center justify-center rounded-full bg-warn/10 text-warn flex-shrink-0 mt-0.5">
                  <i className="ri-fire-line text-sm" />
                </span>
                <div>
                  <div className="text-sm font-medium text-text-primary">Why the scoring matters</div>
                  <div className="text-xs text-text-secondary mt-0.5 leading-relaxed">
                    Classification, severity, and next action are the same three judgement calls
                    engineers make during real incidents. This is practice with a feedback loop.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-bg-panel border border-border-subtle rounded-xl p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-semibold text-text-primary text-base flex items-center gap-2">
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-medal-line text-accent" />
                </span>
                Top Engineers This Week
              </h3>
              <Link
                to="/leaderboard"
                className="text-xs text-accent hover:text-accent-hover transition-colors cursor-pointer whitespace-nowrap"
              >
                View all →
              </Link>
            </div>
            <div className="space-y-2">
              {topThree.map((entry, i) => (
                <div
                  key={entry.rank}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-bg-elevated transition-colors"
                >
                  <span
                    className={`w-7 text-center font-mono font-bold text-sm flex-shrink-0 ${
                      i === 0
                        ? 'text-accent'
                        : i === 1
                          ? 'text-text-secondary'
                          : i === 2
                            ? 'text-warn'
                            : 'text-text-muted'
                    }`}
                  >
                    {entry.rank}
                  </span>
                  <div
                    className={`w-8 h-8 rounded-full bg-gradient-to-br ${
                      AVATAR_GRADIENTS[i % AVATAR_GRADIENTS.length]
                    } flex items-center justify-center text-xs font-bold text-white flex-shrink-0`}
                  >
                    {entry.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-text-primary truncate">
                      {entry.name}
                    </div>
                    <div className="text-xs text-text-muted">{entry.badge}</div>
                  </div>
                  <span className="font-mono font-semibold text-accent text-sm flex-shrink-0">
                    {entry.score}
                  </span>
                </div>
              ))}
            </div>
            <Link
              to="/challenge"
              className="mt-5 flex items-center justify-center gap-2 w-full py-3 bg-accent/10 hover:bg-accent/20 text-accent font-medium text-sm rounded-lg transition-all duration-200 cursor-pointer"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-play-fill" />
              </span>
              Join the leaderboard — play now
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}